import { Fragment, Node as ProseMirrorNode } from "prosemirror-model";

import {
  MarkName,
  NodeName,
  booleanAttr,
  nodeType,
  numberAttr,
  stringAttr,
} from "../schema/index.js";
import { parseMarkdown } from "../markdown/parse.js";
import { serializeWithBlockRanges } from "../markdown/serialize.js";

/* -------------------------------------------------------------- identifiers */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URL_ID_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const URL_ID_LENGTH = 10;
/** The resolver accepts 10–15 trailing alphanumerics as a short id. */
const SLUG_TAIL = /(?:^|-)([A-Za-z0-9]{10,15})$/;

export function isUuid(value: string): boolean {
  return UUID.test(value);
}

export function generateUrlId(): string {
  const bytes = new Uint8Array(URL_ID_LENGTH);
  globalThis.crypto.getRandomValues(bytes);
  let result = "";
  for (const byte of bytes) result += URL_ID_ALPHABET.charAt(byte % URL_ID_ALPHABET.length);
  return result;
}

/**
 * Lower-cases, strips periods, and collapses every other run of
 * non-alphanumerics into a single hyphen. An empty result becomes `untitled`,
 * which is what gives drafts a `/doc/untitled-<urlId>` address.
 */
export function slugify(title: string): string {
  const slug = title
    .replace(/\./g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug === "" ? "untitled" : slug;
}

export function documentUrl(title: string, urlId: string): string {
  return `/doc/${slugify(title)}-${urlId}`;
}

export interface DocumentIdentifier {
  kind: "uuid" | "urlId";
  value: string;
}

/**
 * Resolves the `/doc/…` path segment: a syntactically valid UUID is an id,
 * otherwise the trailing short identifier of the slug is the `urlId`.
 */
export function parseDocumentIdentifier(slugOrId: string): DocumentIdentifier | null {
  const trimmed = slugOrId.trim();
  if (trimmed === "") return null;
  if (isUuid(trimmed)) return { kind: "uuid", value: trimmed };
  const tail = SLUG_TAIL.exec(trimmed)?.[1];
  if (tail !== undefined) return { kind: "urlId", value: tail };
  return null;
}

/* ---------------------------------------------------------------- anchors */

/**
 * The heading anchor slug keeps a different rule from the document slug:
 * punctuation is removed outright and only whitespace runs become hyphens.
 */
export function headingSlug(text: string): string {
  return text
    .replace(/[!"#$%&'.()*+,/:;<=>?@[\]\\^_`{|}~]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");
}

export interface HeadingAnchor {
  level: number;
  text: string;
  id: string;
}

/** `h-<slug>`, with `-<n>` appended for the nth repeat of the same slug. */
export function headingAnchors(doc: ProseMirrorNode): HeadingAnchor[] {
  const seen = new Map<string, number>();
  const anchors: HeadingAnchor[] = [];
  doc.descendants((node) => {
    if (node.type.name !== NodeName.heading) return true;
    const text = node.textContent;
    const base = `h-${headingSlug(text)}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    anchors.push({
      level: numberAttr(node, "level") ?? 1,
      text,
      id: count === 0 ? base : `${base}-${String(count)}`,
    });
    return false;
  });
  return anchors;
}

/* ------------------------------------------------------------- projections */

function inlineText(node: ProseMirrorNode): string {
  let out = "";
  node.forEach((child) => {
    if (child.isText) {
      if (child.marks.some((mark) => mark.type.name === MarkName.placeholder)) return;
      out += child.text ?? "";
      return;
    }
    switch (child.type.name) {
      case NodeName.br:
        out += "\n";
        break;
      case NodeName.emoji:
        out += `:${stringAttr(child, "data-name") ?? ""}:`;
        break;
      case NodeName.image: {
        const alt = stringAttr(child, "alt");
        out += alt === null || alt === "" ? "(image)" : `(image: ${alt})`;
        break;
      }
      case NodeName.mention: {
        const label = stringAttr(child, "label") ?? "";
        out += stringAttr(child, "type") === "user" ? `@${label}` : label;
        break;
      }
      default:
        out += child.textContent;
        break;
    }
  });
  return out;
}

/** Block-per-line projection used for search indexing and previews. */
export function extractPlainText(doc: ProseMirrorNode): string {
  const lines: string[] = [];
  const visit = (node: ProseMirrorNode): void => {
    if (node.isTextblock) {
      lines.push(inlineText(node));
      return;
    }
    switch (node.type.name) {
      case NodeName.attachment:
      case NodeName.video:
        lines.push(stringAttr(node, "title") ?? "");
        return;
      case NodeName.embed:
        lines.push(stringAttr(node, "href") ?? "");
        return;
      case NodeName.hr:
        return;
      default:
        node.forEach(visit);
    }
  };
  doc.forEach(visit);
  return lines.join("\n");
}

export interface TaskCounts {
  completed: number;
  total: number;
}

/** Counts direct children of every checkbox list, table cells included. */
export function countTasks(doc: ProseMirrorNode): TaskCounts {
  let completed = 0;
  let total = 0;
  doc.descendants((node) => {
    if (node.type.name !== NodeName.checkboxItem) return true;
    total += 1;
    if (booleanAttr(node, "checked")) completed += 1;
    return true;
  });
  return { completed, total };
}

export interface InternalLinks {
  documentIds: string[];
  urlIds: string[];
  collectionIds: string[];
}

function stripOrigin(href: string): string {
  const match = /^https?:\/\/[^/]+(\/.*)$/.exec(href);
  return match?.[1] ?? href;
}

/**
 * Backlink extraction: document mentions plus every link whose href resolves
 * to an internal document or collection URL, in first-seen order.
 */
export function extractInternalLinks(doc: ProseMirrorNode): InternalLinks {
  const documentIds: string[] = [];
  const urlIds: string[] = [];
  const collectionIds: string[] = [];
  const add = (list: string[], value: string): void => {
    if (value !== "" && !list.includes(value)) list.push(value);
  };

  const consider = (href: string): void => {
    const path = stripOrigin(href);
    const docMatch = /^\/doc\/([^/?#]+)/.exec(path);
    if (docMatch?.[1]) {
      const identifier = parseDocumentIdentifier(decodeURIComponent(docMatch[1]));
      if (identifier?.kind === "uuid") add(documentIds, identifier.value);
      else if (identifier) add(urlIds, identifier.value);
      return;
    }
    const collectionMatch = /^\/collection\/([^/?#]+)/.exec(path);
    if (collectionMatch?.[1]) add(collectionIds, decodeURIComponent(collectionMatch[1]));
  };

  doc.descendants((node) => {
    if (node.type.name === NodeName.mention) {
      const type = stringAttr(node, "type");
      const modelId = stringAttr(node, "modelId") ?? "";
      if (type === "document") add(documentIds, modelId);
      if (type === "collection") add(collectionIds, modelId);
      return true;
    }
    for (const mark of node.marks) {
      if (mark.type.name !== MarkName.link) continue;
      const href = stringAttr(mark, "href");
      if (href !== null) consider(href);
    }
    return true;
  });

  return { documentIds, urlIds, collectionIds };
}

/* ------------------------------------------------------------- edit modes */

function replaceRange(
  doc: ProseMirrorNode,
  firstBlock: number,
  lastBlock: number,
  replacement: Fragment,
): ProseMirrorNode {
  const before: ProseMirrorNode[] = [];
  const after: ProseMirrorNode[] = [];
  doc.forEach((child, _offset, index) => {
    if (index < firstBlock) before.push(child);
    else if (index > lastBlock) after.push(child);
  });
  const content = Fragment.fromArray([...before, ...replacement.content, ...after]);
  return nodeType(NodeName.doc).createChecked(null, content);
}

/**
 * `editMode: "patch"`. Serializes the document with a per-block position map,
 * finds the first literal occurrence of `findText`, expands the match to the
 * top-level blocks it overlaps, substitutes the replacement inside that region
 * and re-parses it. Returns null when the text is not found.
 */
export function patchMarkdown(
  doc: ProseMirrorNode,
  findText: string,
  replacement: string,
): ProseMirrorNode | null {
  if (findText === "") return null;
  const { markdown, blocks } = serializeWithBlockRanges(doc);
  const at = markdown.indexOf(findText);
  if (at === -1) return null;
  const end = at + findText.length;

  const overlapping = blocks.filter((block) => block.from < end && block.to > at);
  if (overlapping.length === 0) return null;
  const first = overlapping[0];
  const last = overlapping[overlapping.length - 1];
  if (!first || !last) return null;

  const region = markdown.slice(first.from, last.to);
  const patched = region.slice(0, at - first.from) + replacement + region.slice(end - first.from);
  return replaceRange(doc, first.index, last.index, parseMarkdown(patched).content);
}

function joinFragments(
  head: ProseMirrorNode[],
  tail: ProseMirrorNode[],
  merge: boolean,
): ProseMirrorNode {
  if (merge) {
    const last = head[head.length - 1];
    const first = tail[0];
    if (
      last?.type.name === NodeName.paragraph &&
      first?.type.name === NodeName.paragraph &&
      last.content.size > 0 &&
      first.content.size > 0
    ) {
      const merged = last.type.create(last.attrs, last.content.append(first.content));
      return nodeType(NodeName.doc).createChecked(
        null,
        Fragment.fromArray([...head.slice(0, -1), merged, ...tail.slice(1)]),
      );
    }
  }
  return nodeType(NodeName.doc).createChecked(null, Fragment.fromArray([...head, ...tail]));
}

function childrenOf(doc: ProseMirrorNode): ProseMirrorNode[] {
  const children: ProseMirrorNode[] = [];
  doc.forEach((child) => children.push(child));
  return children;
}

/**
 * `editMode: "append"`. The new text joins the document's last paragraph when
 * it does not itself start on a new line, so an agent can extend a sentence
 * rather than always starting a block.
 */
export function appendMarkdown(doc: ProseMirrorNode, text: string): ProseMirrorNode {
  const merge = !/^\s*\n/.test(text);
  return joinFragments(childrenOf(doc), childrenOf(parseMarkdown(text)), merge);
}

/** `editMode: "prepend"`, the mirror image of {@link appendMarkdown}. */
export function prependMarkdown(doc: ProseMirrorNode, text: string): ProseMirrorNode {
  const merge = !/\n\s*$/.test(text);
  return joinFragments(childrenOf(parseMarkdown(text)), childrenOf(doc), merge);
}
